import { ToggleGroup } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { useReadOnly } from './read-only-context';
import { Tooltip } from './tooltip';

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Optional explanatory tooltip. */
  hint?: ReactNode;
  shortcut?: readonly string[];
  disabled?: boolean;
}

export interface SegmentedProps<T extends string> {
  /** Accessible name for the group. */
  label: string;
  value: T;
  options: readonly SegmentedOption<T>[];
  onValueChange: (value: T) => void;
  className?: string;
}

/** Single-select segmented control. Unlike a raw ToggleGroup, it can never be emptied. */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onValueChange,
  className,
}: SegmentedProps<T>) {
  // Read-only: the selected option stays readable; the others can't be picked.
  const readOnly = useReadOnly();
  return (
    <ToggleGroup.Root
      type="single"
      aria-label={label}
      value={value}
      onValueChange={(next) => {
        const option = options.find((o) => o.value === next);
        if (option) onValueChange(option.value);
      }}
      className={cn(
        'inline-flex h-7 items-center gap-0.5 rounded-md border border-border-subtle bg-bg-1 p-0.5',
        className,
      )}
    >
      {options.map((option) => {
        const item = (
          <ToggleGroup.Item
            key={option.value}
            value={option.value}
            disabled={option.disabled === true || (readOnly && option.value !== value)}
            className={cn(
              'h-full rounded-[4px] px-2 text-xs font-medium whitespace-nowrap text-text-3 transition-colors duration-100 outline-none',
              'hover:text-text-1 focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-45 disabled:hover:text-text-3',
              // ARIA, not data-state: the optional Tooltip trigger overwrites data-state.
              'aria-checked:bg-bg-4 aria-checked:text-text-1 aria-checked:shadow-[0_0_0_1px_var(--border-strong)]',
            )}
          >
            {option.label}
          </ToggleGroup.Item>
        );
        return option.hint === undefined ? (
          item
        ) : (
          <Tooltip
            key={option.value}
            content={option.hint}
            {...(option.shortcut ? { shortcut: option.shortcut } : {})}
          >
            {item}
          </Tooltip>
        );
      })}
    </ToggleGroup.Root>
  );
}
